import * as React from "react";
import { cn } from "@/lib/cn";

/**
 * The first cell of a list row: a mark, a name, and a line under it. Used for a
 * company, a site and a person, which is every table in the console.
 *
 * **`min-w-0` on the text column is the entire point** (see `.identity__text`).
 * A flex child's default `min-width` is `auto` - "at least as wide as my
 * content" - so `truncate` on a long email address does nothing, the cell grows
 * instead, and the table overflows sideways.
 *
 * `subtitleOnlyBelow` handles the responsive half of these rows: the same value
 * has its own column on a wide screen and collapses under the name on a narrow
 * one, so it must be hidden here at precisely the breakpoint the column appears.
 */
export function Identity({
  media,
  title,
  subtitle,
  subtitleOnlyBelow,
  className,
}: {
  /** An `IconTile`, an `Avatar`, or nothing. */
  media?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Hide the subtitle at and above this breakpoint, where it has its own column. */
  subtitleOnlyBelow?: "sm" | "md";
  className?: string;
}) {
  return (
    <div className={cn("identity", className)}>
      {media}
      <span className="identity__text">
        <span className="identity__title">{title}</span>
        {subtitle && (
          <span
            className={cn(
              "identity__subtitle",
              subtitleOnlyBelow && `identity__subtitle--only-below-${subtitleOnlyBelow}`
            )}
          >
            {subtitle}
          </span>
        )}
      </span>
    </div>
  );
}
